import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Copy, Check } from 'lucide-react';
import { Button } from 'components/ui/button';
import { Panel } from 'components/ui/panel';
import { GetReferralCode } from 'modules/referrals/api';
import { REFERRAL_COINS } from 'modules/coins/constants';
import { formatCoinCount } from './billingHelpers';

interface CopyFieldProps {
  label: string;
  value?: string;
  /** Codes read larger than links. */
  emphasis?: boolean;
}

/** A read-only value with its own copy button; the button confirms for a moment after copying. */
function CopyField({ label, value, emphasis = false }: CopyFieldProps) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      /* clipboard may be blocked; the value is still visible to copy manually */
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <span className="text-[12.5px] text-muted-foreground">{label}</span>
      <div className="flex gap-2">
        <span
          className={
            emphasis
              ? 'flex h-9 min-w-0 flex-1 items-center truncate rounded-md border border-border bg-muted/40 px-3 font-mono text-[15px] font-semibold text-foreground'
              : 'flex h-9 min-w-0 flex-1 items-center truncate rounded-md border border-border bg-muted/40 px-3 font-mono text-xs text-muted-foreground'
          }
        >
          {value ?? '—'}
        </span>
        <Button variant="outline" className="shrink-0 gap-1.5" onClick={copy} disabled={!value}>
          {copied ? <Check className="h-4 w-4" aria-hidden /> : <Copy className="h-4 w-4" aria-hidden />}
          {copied ? t('copied', 'Copied') : t('copy', 'Copy')}
        </Button>
      </div>
    </div>
  );
}

/** Refer & earn: the org's referral code and shareable signup link, and what a referral pays. */
export function ReferralCard() {
  const { t } = useTranslation();
  const { data: referral } = useQuery({
    queryKey: ['referrals', 'code'],
    queryFn: async () => (await GetReferralCode()).data,
  });

  return (
    <Panel headingLevel="h3" title={t('refer_and_earn', 'Refer & earn')}>
      <p className="flex flex-wrap items-baseline gap-x-1.5">
        <span className="text-xl font-bold tabular-nums text-foreground">{formatCoinCount(REFERRAL_COINS)}</span>
        <span className="text-[13px] text-muted-foreground">
          {t('coins_unit', 'coins')} · {t('per_referral', 'per referral')}
        </span>
      </p>
      <p className="mt-1 text-[13px] leading-5 text-muted-foreground">
        {t('referral_hint', 'Share your code or link. When a referred team makes their first payment, you earn coins.')}
      </p>
      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <CopyField label={t('referral_code_share_label', 'Your referral code')} value={referral?.code} emphasis />
        <CopyField label={t('share_link', 'Share link')} value={referral?.link} />
      </div>
    </Panel>
  );
}

export default ReferralCard;
